/**
 * La note au barème d'un sujet du dimanche, côté parent (`core-api` §3.32).
 *
 * Un modèle a lu la copie question par question et propose un verdict par
 * question ; **le parent confirme**, question par question, et c'est cette
 * confirmation qui écrit la note sur 20 et les observations de maîtrise. Le
 * calcul des points n'est **pas** ici : il vit côté serveur (`points_de`), comme
 * le barème d'un exercice vit dans `EntrainementVerdict.score`. Une seconde
 * table de correspondance écrite dans la coquille finirait par diverger.
 *
 * Fonctions pures, exportées pour être testées sans monter l'écran.
 */

import type { QuestionDeSujetOut, VerdictDeQuestionIn } from "@/api/schema";

export type VerdictDeQuestion = VerdictDeQuestionIn["verdict"];

const CONFIRMABLES: ReadonlySet<string> = new Set(["juste", "moitie", "faux", "pas_fait"]);

/** Les verdicts pré-armés depuis la lecture du modèle. « illisible » ne
 *  pré-arme rien : la question revient au parent entière. */
export function preArmes(
  questions: readonly QuestionDeSujetOut[],
): Record<string, VerdictDeQuestion | null> {
  return Object.fromEntries(
    questions.map((question) => {
      const propose = question.proposition?.verdict;
      return [
        question.numero,
        propose && CONFIRMABLES.has(propose) ? (propose as VerdictDeQuestion) : null,
      ];
    }),
  );
}

/** Le corps à envoyer, ou `null` tant qu'une question n'a pas de verdict : le
 *  serveur refuse une note partielle, l'écran ne la propose pas. */
export function corpsDeLaNote(
  questions: readonly QuestionDeSujetOut[],
  verdicts: Record<string, VerdictDeQuestion | null>,
): VerdictDeQuestionIn[] | null {
  const corps: VerdictDeQuestionIn[] = [];
  for (const question of questions) {
    const verdict = verdicts[question.numero];
    if (!verdict) return null;
    corps.push({ numero: question.numero, verdict });
  }
  return corps;
}

/** Combien de questions attendent encore un verdict. */
export function restantes(
  questions: readonly QuestionDeSujetOut[],
  verdicts: Record<string, VerdictDeQuestion | null>,
): number {
  return questions.filter((question) => !verdicts[question.numero]).length;
}

/** « 2 pts », « 1,5 pt » : les points d'une question, écrits à la française. */
export function points(valeur: number): string {
  const texte = String(Math.round(valeur * 100) / 100).replace(".", ",");
  return `${texte} pt${valeur >= 2 ? "s" : ""}`;
}

/** « 11,67 / 20 ». */
export function surVingt(note: number): string {
  return `${String(Math.round(note * 100) / 100).replace(".", ",")} / 20`;
}
